import React, { useCallback, useReducer } from 'react';
import { createUserReducer } from './createUserReducer';
import { IItemForGetShortInfo, TFileId, IServerError, IUserEmailValidationError, ICreateUser } from 'types';
import {
	createUserInitialState,
	CreateUserContext,
	TCreateUserSelectedItem,
	TTypeOfCreateUserModalWindow,
	TSourceUrlLocally,
} from './createUserContext';
import { CreateUserActionTypes } from './createUserActions';

const CreateUserState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createUserReducer, createUserInitialState);

	const setFile = (fileId: TFileId, sourceUrlLocally: TSourceUrlLocally) => {
		dispatch({
			type: CreateUserActionTypes.SET_FILE,
			payload: {
				fileId: fileId,
				sourceUrlLocally: sourceUrlLocally,
			},
		});
	};

	const changeName = useCallback((value: string) => {
		dispatch({
			type: CreateUserActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const changeSurName = useCallback((value: string) => {
		dispatch({
			type: CreateUserActionTypes.CHANGE_SURNAME,
			payload: {
				surName: value,
			},
		});
	}, []);

	const changeEmail = useCallback((value: string) => {
		dispatch({
			type: CreateUserActionTypes.CHANGE_EMAIL,
			payload: {
				email: value,
			},
		});
	}, []);

	const changePhoneNumber = useCallback((value: string) => {
		dispatch({
			type: CreateUserActionTypes.CHANGE_PHONE_NUMBER,
			payload: {
				phoneNumber: value,
			},
		});
	}, []);

	const setFetchedRoles = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateUserActionTypes.SET_FETCHED_ROLES,
			payload: {
				roles: value,
			},
		});
	}, []);

	const setRole = useCallback((value: TCreateUserSelectedItem) => {
		dispatch({
			type: CreateUserActionTypes.SET_ROLE,
			payload: {
				role: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateUserActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setCustomer = useCallback((value: TCreateUserSelectedItem) => {
		dispatch({
			type: CreateUserActionTypes.SET_CUSTOMER,
			payload: {
				selectedCustomer: value,
			},
		});
	}, []);

	const setCustomers = useCallback((value: string[]) => {
		dispatch({
			type: CreateUserActionTypes.SET_CUSTOMERS,
			payload: {
				selectedCustomers: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateUserActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: CreateUserActionTypes.SET_RESTAURANTS,
			payload: {
				selectedRestaurants: value,
			},
		});
	}, []);

	const changePassword = useCallback((value: string) => {
		dispatch({
			type: CreateUserActionTypes.CHANGE_PASSWORD,
			payload: {
				password: value,
			},
		});
	}, []);

	const changePasswordConfirm = useCallback((value: string) => {
		dispatch({
			type: CreateUserActionTypes.CHANGE_PASSWORD_CONFIRM,
			payload: {
				passwordConfirm: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: ICreateUser | null) => {
		dispatch({
			type: CreateUserActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfCreateUserModalWindow) => {
		dispatch({
			type: CreateUserActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<IUserEmailValidationError> | null) => {
		dispatch({
			type: CreateUserActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateUserActionTypes.RESET_FIELDS }), []);

	return (
		<CreateUserContext.Provider
			value={{
				setFile,
				changeName,
				changeSurName,
				changeEmail,
				changePhoneNumber,
				setFetchedRoles,
				setRole,
				setFetchedCustomers,
				setCustomer,
				setCustomers,
				setFetchedRestaurants,
				setRestaurants,
				changePassword,
				changePasswordConfirm,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				fileId: state.fileId,
				sourceUrlLocally: state.sourceUrlLocally,
				name: state.name,
				surName: state.surName,
				email: state.email,
				phoneNumber: state.phoneNumber,
				fetchedRoles: state.fetchedRoles,
				selectedRole: state.selectedRole,
				fetchedCustomers: state.fetchedCustomers,
				selectedCustomer: state.selectedCustomer,
				selectedCustomers: state.selectedCustomers,
				fetchedRestaurants: state.fetchedRestaurants,
				selectedRestaurants: state.selectedRestaurants,
				password: state.password,
				passwordConfirm: state.passwordConfirm,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</CreateUserContext.Provider>
	);
};

export default CreateUserState;
